import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const UserAccountMenu = ({ user, onClose }) => {
  console.log('UserAccountMenu rendered with user:', user);
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState('account');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [systemInfo, setSystemInfo] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKeyData, setNewApiKeyData] = useState(null);
  
  // États pour les formulaires
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    phone: '',
    port_number: '',
    title: '',
    profile_photo: ''
  });
  
  const [preferences, setPreferences] = useState({
    theme: 'light',
    language: 'fr',
    timezone: 'Africa/Abidjan',
    date_format: 'DD/MM/YYYY',
    notifications: {
      email_notifications: true,
      in_app_notifications: true
    }
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        port_number: user.port_number || '',
        title: user.title || '',
        profile_photo: user.profile_photo || ''
      });
      loadSystemInfo();
      loadUserPreferences();
      loadApiKeys();
      check2FAStatus();
      loadSessionInfo();
    }
  }, [user]);

  const loadSystemInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/system/info`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSystemInfo(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations système:', error);
    }
  };

  const loadSessionInfo = () => {
    // Simuler les informations de session en l'absence de loginTime
    const now = new Date();
    const sessionDuration = 30; // 30 minutes par défaut
    
    setSessionInfo({
      loginTime: new Date(now.getTime() - sessionDuration * 60 * 1000),
      duration: sessionDuration,
      lastActivity: new Date(now.getTime() - 5 * 60 * 1000) // 5 minutes ago
    });
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profil mis à jour avec succès!' });
        showToast('✔️ Profil mis à jour!', 'success');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la mise à jour du profil' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    }
    setLoading(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Vérification de la taille (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'La taille du fichier ne doit pas dépasser 2MB' });
        return;
      }
      
      // Vérification du type de fichier
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Veuillez sélectionner un fichier image valide' });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        setProfileData({ ...profileData, profile_photo: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/preferences`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences({ ...preferences, ...data });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des préférences:', error);
    }
  };

  const loadApiKeys = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/api-keys`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clés API:', error);
    }
  };

  const markApiKeyAsViewed = async (keyId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/api-keys/${keyId}/mark-viewed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Erreur lors du marquage de la clé comme vue:', error);
    }
  };

  const handleApiKeyModalClose = async () => {
    if (newApiKeyData) {
      // Mark the key as viewed when modal is closed
      await markApiKeyAsViewed(newApiKeyData.id);
      
      // Add the key to the list (without the full key)
      const viewedKey = { ...newApiKeyData, key: null, has_been_viewed: true };
      setApiKeys([viewedKey, ...apiKeys]);
    }
    
    setShowApiKeyModal(false);
    setNewApiKeyData(null);
    showToast('Clé API enregistrée en sécurité!', 'success');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Clé API copiée dans le presse-papier!', 'success');
    }).catch(() => {
      showToast('Erreur lors de la copie', 'error');
    });
  };

  const check2FAStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFactorEnabled(data.enabled);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du 2FA:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Mot de passe modifié avec succès!' });
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        setShowPasswordForm(false);
        showToast('Mot de passe modifié avec succès!', 'success');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors du changement de mot de passe' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    }
    setLoading(false);
  };

  const toggle2FA = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/2fa/${twoFactorEnabled ? 'disable' : 'enable'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setTwoFactorEnabled(!twoFactorEnabled);
        const statusText = !twoFactorEnabled ? 'activée' : 'désactivée';
        setMessage({ 
          type: 'success', 
          text: `Double authentification ${statusText} avec succès!` 
        });
        showToast(`2FA ${statusText}!`, 'success');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la modification du 2FA' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    }
    setLoading(false);
  };

  const generateApiKey = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(apiKeyForm)
      });

      if (response.ok) {
        const newKey = await response.json();
        
        // Show the secure modal with the new key
        setNewApiKeyData(newKey);
        setShowApiKeyModal(true);
        
        // Reset form
        setApiKeyForm({ name: '', description: '', permissions: [] });
        setMessage({ type: 'success', text: 'Clé API générée avec succès!' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la génération de la clé API' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    }
    setLoading(false);
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette clé API ?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setApiKeys(apiKeys.filter(key => key.id !== keyId));
        setMessage({ type: 'success', text: 'Clé API supprimée avec succès!' });
        showToast('Clé API supprimée!', 'success');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression de la clé API' });
    }
  };

  const savePreferences = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Préférences sauvegardées avec succès!' });
        showToast('✔️ Préférences enregistrées!', 'success');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur' });
    }
    setLoading(false);
  };

  const showToast = (message, type) => {
    // Créer et afficher un toast de notification
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 z-50 px-4 py-2 rounded-md text-white ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } shadow-lg transition-all duration-300`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  };

  const getSecurityAlerts = () => {
    const alerts = [];
    
    // Vérifier le 2FA
    if (!twoFactorEnabled) {
      alerts.push({
        type: 'error',
        message: 'Double authentification non activée',
        icon: '🔒'
      });
    }
    
    // Vérifier la photo de profil
    if (!user?.profile_photo) {
      alerts.push({
        type: 'info',
        message: 'Photo de profil non configurée',
        icon: '📷'
      });
    }
    
    return alerts;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const renderAccountSection = () => (
    <div className="space-y-6">
      {/* Résumé en haut */}
      {sessionInfo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">📊 Résumé de session</h4>
          <div className="text-sm text-blue-700">
            <div>Connecté depuis : {formatDuration(sessionInfo.duration)}</div>
            <div>Dernière activité : il y a {Math.floor((new Date() - sessionInfo.lastActivity) / (1000 * 60))} min</div>
          </div>
        </div>
      )}

      {/* Profil utilisateur */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">👤 Informations du profil</h4>
        
        {/* Photo de profil */}
        <div className="mb-6 flex items-center space-x-4">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden">
            {profileData.profile_photo ? (
              <img 
                src={profileData.profile_photo} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-2xl">
                {(profileData.first_name || profileData.full_name || 'U').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <button
              onClick={() => document.getElementById('profile-photo-input').click()}
              className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              📷 Changer la photo
            </button>
            <input
              id="profile-photo-input"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <p className="text-xs text-gray-500 mt-1">Formats: PNG, JPG, JPEG. Max: 2MB</p>
          </div>
        </div>

        {/* Formulaire profil */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Prénom</label>
            <input
              type="text"
              value={profileData.first_name}
              onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Votre prénom"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nom</label>
            <input
              type="text"
              value={profileData.last_name}
              onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Votre nom"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Titre/Position</label>
            <input
              type="text"
              value={profileData.title}
              onChange={(e) => setProfileData({...profileData, title: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Votre titre ou position"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({...profileData, email: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="votre.email@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Numéro de téléphone</label>
            <input
              type="tel"
              value={profileData.phone}
              onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+225 XX XX XX XX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Numéro de port</label>
            <input
              type="text"
              value={profileData.port_number}
              onChange={(e) => setProfileData({...profileData, port_number: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Port numéro"
            />
          </div>
        </div>

        <button
          onClick={saveProfile}
          disabled={loading}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
        >
          <span className="mr-2">✔️</span>
          {loading ? 'Sauvegarde...' : 'Sauvegarder le profil'}
        </button>
      </div>

      {/* Sécurité du compte */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold flex items-center">
            🔐 Sécurité du compte
          </h4>
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            🔄 Modifier le mot de passe
          </button>
        </div>

        {showPasswordForm && (
          <form onSubmit={handlePasswordChange} className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Mot de passe actuel</label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
              <input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Modification...' : 'Modifier'}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* 2FA Toggle avec indicateur de statut */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h5 className="text-sm font-medium flex items-center">
                🔒 Double authentification (2FA)
                {twoFactorEnabled ? (
                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">ACTIF</span>
                ) : (
                  <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">INACTIF</span>
                )}
              </h5>
              <p className="text-xs text-gray-600">Sécurisez votre compte avec un code par email</p>
            </div>
            <button
              onClick={toggle2FA}
              disabled={loading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                twoFactorEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Alertes de sécurité */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4">🔒 Alertes de sécurité</h4>
        <div className="space-y-2">
          {getSecurityAlerts().map((alert, index) => (
            <div 
              key={index}
              className={`flex items-center p-2 rounded text-sm ${
                alert.type === 'error' ? 'bg-red-50 text-red-700' :
                alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                'bg-blue-50 text-blue-700'
              }`}
            >
              <span className="mr-2">{alert.icon}</span>
              {alert.message}
            </div>
          ))}
          {getSecurityAlerts().length === 0 && (
            <div className="flex items-center p-2 rounded text-sm bg-green-50 text-green-700">
              <span className="mr-2">✅</span>
              Aucune alerte de sécurité
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDevelopersSection = () => (
    <div className="space-y-6">
      {/* Documentation API */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">📚 Documentation API</h4>
        <p className="text-gray-600 mb-4">
          Accédez à la documentation complète de l'API Baobab Connect pour intégrer nos services.
        </p>
        <div className="space-y-2">
          <a
            href="/api/docs"
            target="_blank"
            className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
          >
            <span className="mr-2">📄</span>
            Documentation API CRM
          </a>
          <a
            href="/api/transport/docs"
            target="_blank"
            className="inline-flex items-center px-4 py-2 border border-green-600 text-green-600 rounded-md hover:bg-green-50 ml-2"
          >
            <span className="mr-2">🚛</span>
            Documentation API Transport
          </a>
        </div>
      </div>

      {/* Gestion des clés API personnelles */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">🔐 Clé API personnelle</h4>
        
        {/* Génération de nouvelle clé */}
        <form onSubmit={generateApiKey} className="mb-4 p-3 bg-gray-50 rounded">
          <h5 className="text-sm font-medium mb-2">Générer une nouvelle clé API</h5>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input
              type="text"
              placeholder="Nom de la clé"
              value={apiKeyForm.name}
              onChange={(e) => setApiKeyForm({...apiKeyForm, name: e.target.value})}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              required
            />
            <input
              type="text"
              placeholder="Description"
              value={apiKeyForm.description}
              onChange={(e) => setApiKeyForm({...apiKeyForm, description: e.target.value})}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Génération...' : '🔑 Générer'}
          </button>
        </form>

        {/* Liste des clés existantes */}
        <div className="space-y-2">
          <h5 className="text-sm font-medium">Clés API existantes</h5>
          {apiKeys.map((key) => (
            <div key={key.id} className="flex justify-between items-center p-2 border border-gray-200 rounded">
              <div className="flex-1">
                <div className="text-sm font-medium">{key.name}</div>
                <div className="text-xs text-gray-600">{key.description}</div>
                <div className="text-xs font-mono bg-gray-100 px-2 py-1 rounded mt-1 flex items-center justify-between">
                  <span>{key.masked_key || (key.key ? `${key.key.substring(0, 8)}***...***` : 'Clé masquée')}</span>
                  {key.has_been_viewed && (
                    <span className="text-xs text-red-600 ml-2">👁️ Vue</span>
                  )}
                </div>
                {key.has_been_viewed && (
                  <div className="text-xs text-orange-600 mt-1 flex items-center">
                    <span className="mr-1"></span>
                    Cette clé a été visualisée et ne peut plus être affichée en entier
                  </div>
                )}
              </div>
              <button
                onClick={() => deleteApiKey(key.id)}
                className="text-red-600 hover:text-red-800 text-sm ml-2"
              >
                🗑️ Supprimer
              </button>
            </div>
          ))}
          {apiKeys.length === 0 && (
            <p className="text-gray-500 text-sm">Aucune clé API générée</p>
          )}
        </div>
      </div>

      {/* Historique d'activité personnel */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">📊 Historique d'activité</h4>
        <p className="text-gray-600 mb-2">
          Consultez votre activité personnelle sur la plateforme.
        </p>
        <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center">
          <span className="mr-1">🧭</span>
          Voir l'historique complet →
        </button>
      </div>
    </div>
  );

  const renderDisplaySection = () => (
    <div className="space-y-6">
      {/* Préférences d'affichage */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">⚙️ Compte & Affichage</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Langue</label>
            <select
              value={preferences.language}
              onChange={(e) => setPreferences({...preferences, language: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="fr">🇫🇷 Français</option>
              <option value="en">🇺🇸 English</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Fuseau horaire</label>
            <select
              value={preferences.timezone}
              onChange={(e) => setPreferences({...preferences, timezone: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="Africa/Abidjan">🌍 Abidjan (GMT)</option>
              <option value="Europe/Paris">🇫🇷 Paris (CET)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Thème</label>
            <select
              value={preferences.theme}
              onChange={(e) => setPreferences({...preferences, theme: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="light">☀️ Clair</option>
              <option value="dark">🌙 Sombre</option>
              <option value="auto">🔄 Automatique</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Format de date</label>
            <select
              value={preferences.date_format}
              onChange={(e) => setPreferences({...preferences, date_format: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="DD/MM/YYYY">📅 DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">📅 MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">📅 YYYY-MM-DD</option>
            </select>
          </div>
        </div>
        <button
          onClick={savePreferences}
          disabled={loading}
          className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
        >
          <span className="mr-2">✔️</span>
          {loading ? 'Sauvegarde...' : 'Sauvegarder les préférences'}
        </button>
      </div>

      {/* Notifications */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">🔔 Notifications</h4>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Notifications par email</span>
            <input
              type="checkbox"
              checked={preferences.notifications.email_notifications}
              onChange={(e) => setPreferences({
                ...preferences,
                notifications: {...preferences.notifications, email_notifications: e.target.checked}
              })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Notifications dans l'app</span>
            <input
              type="checkbox"
              checked={preferences.notifications.in_app_notifications}
              onChange={(e) => setPreferences({
                ...preferences,
                notifications: {...preferences.notifications, in_app_notifications: e.target.checked}
              })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSupportSection = () => (
    <div className="space-y-6">
      {/* Centre de support */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">💬 Centre de support</h4>
        <p className="text-gray-600 mb-4">
          Besoin d'aide ? Contactez notre équipe de support.
        </p>
        <div className="space-y-2">
          <a
            href="mailto:support@baobabco.net"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <span className="mr-2">📤</span>
            Contacter le support
          </a>
        </div>
      </div>

      {/* Documentation utilisateur */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">📖 Documentation</h4>
        <div className="space-y-2">
          <a href="#" className="block text-blue-600 hover:text-blue-800 text-sm flex items-center">
            <span className="mr-2">🚀</span>
            Guide de démarrage rapide
          </a>
          <a href="#" className="block text-blue-600 hover:text-blue-800 text-sm flex items-center">
            <span className="mr-2">❓</span>
            FAQ - Questions fréquentes
          </a>
          <a href="#" className="block text-blue-600 hover:text-blue-800 text-sm flex items-center">
            <span className="mr-2">🎥</span>
            Tutoriels vidéo
          </a>
          <a href="#" className="block text-blue-600 hover:text-blue-800 text-sm flex items-center">
            <span className="mr-2">📚</span>
            Guide d'utilisation avancé
          </a>
        </div>
      </div>

      {/* Signalement de bugs */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">🐛 Signaler un problème</h4>
        <p className="text-gray-600 mb-2 text-sm">
          Aidez-nous à améliorer Baobab Connect en signalant les bugs ou en proposant des améliorations.
        </p>
        <button className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 text-sm flex items-center">
          <span className="mr-2">🐛</span>
          Signaler un problème
        </button>
      </div>
    </div>
  );

  const renderSystemSection = () => (
    <div className="space-y-6">
      {/* Informations système */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold mb-4 flex items-center">🖥️ Système & Application</h4>
        {systemInfo ? (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">📱</span>
                Version de l'application:
              </span>
              <span className="font-medium">v{systemInfo.version} — {new Date().toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">🕒</span>
                Heure système:
              </span>
              <span className="font-medium">
                {new Date().toLocaleString('fr-FR', { 
                  timeZone: preferences.timezone,
                  dateStyle: 'short',
                  timeStyle: 'medium'
                })}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">🌍</span>
                Fuseau horaire actif:
              </span>
              <span className="font-medium">Abidjan (GMT+0)</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">⏱️</span>
                Durée de session:
              </span>
              <span className="font-medium">
                {sessionInfo ? formatDuration(sessionInfo.duration) : 'Chargement...'}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">🔄</span>
                État de la session:
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                Active
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="text-gray-600 flex items-center">
                <span className="mr-2">👤</span>
                Dernière activité:
              </span>
              <span className="font-medium">
                {sessionInfo ? `il y a ${Math.floor((new Date() - sessionInfo.lastActivity) / (1000 * 60))} min` : 'Maintenant'}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Chargement des informations système...</p>
        )}
      </div>
    </div>
  );

  const sections = [
    { id: 'account', label: 'Mon Compte', icon: '👤' },
    { id: 'developers', label: 'Développeurs & API', icon: '🧩' },
    { id: 'display', label: 'Compte & Affichage', icon: '⚙️' },
    { id: 'support', label: 'Support & Aide', icon: '💬' },
    { id: 'system', label: 'Système & Application', icon: 'ℹ️' }
  ];

  return (
    <div className="user-account-menu">
      <div 
        className="user-account-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full">
          {/* Header */}
          <div className="w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {user?.full_name || 'Utilisateur'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {user?.role === 'admin' ? 'Administrateur Commercial' : 'Utilisateur'} • {user?.email}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>

            <div className="flex h-[calc(100vh-120px)]">
              {/* Sidebar navigation */}
              <div className="w-72 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-lg">{section.icon}</span>
                      <span className="text-sm font-medium">{section.label}</span>
                    </button>
                  ))}
                  
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    {/* Déconnexion */}
                    <button
                      onClick={() => {
                        logout();
                        onClose();
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <span className="text-lg">🚪</span>
                      <span className="text-sm font-medium">Se déconnecter</span>
                    </button>
                  </div>
                </nav>
              </div>

              {/* Content area */}
              <div className="flex-1 p-6 overflow-y-auto">
                {message.text && (
                  <div className={`mb-4 p-3 rounded-md ${
                    message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {message.text}
                  </div>
                )}

                {activeSection === 'account' && renderAccountSection()}
                {activeSection === 'developers' && renderDevelopersSection()}
                {activeSection === 'display' && renderDisplaySection()}
                {activeSection === 'support' && renderSupportSection()}
                {activeSection === 'system' && renderSystemSection()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de sécurité pour nouvelle clé API */}
      {showApiKeyModal && newApiKeyData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{zIndex: 10300}}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                🔐 Clé API générée avec succès !
              </h3>
              <button
                onClick={handleApiKeyModalClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <span className="text-red-600 text-xl mr-3"></span>
                  <div>
                    <h4 className="text-red-800 font-semibold mb-2">IMPORTANT - Attention Sécurité</h4>
                    <ul className="text-red-700 text-sm space-y-1">
                      <li>• Cette clé ne sera affichée qu'<strong>UNE SEULE FOIS</strong></li>
                      <li>• Copiez-la maintenant et conservez-la en lieu sûr</li>
                      <li>• Une fois cette fenêtre fermée, vous ne pourrez plus la voir</li>
                      <li>• Si vous perdez cette clé, vous devrez en générer une nouvelle</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la clé : <strong>{newApiKeyData.name}</strong>
                </label>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre clé API :
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 p-3 bg-gray-100 border rounded-md font-mono text-sm break-all">
                    {newApiKeyData.key}
                  </div>
                  <button
                    onClick={() => copyToClipboard(newApiKeyData.key)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                  >
                    📋 Copier
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="mr-2">🕒</span>
                Générée le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
              </div>
              <button
                onClick={handleApiKeyModalClose}
                className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 flex items-center"
              >
                <span className="mr-2">✅</span>
                J'ai copié la clé, fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountMenu;